import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Plane, Shield, Clock, Star, CheckCircle, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

export default function Home() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1758544540794-1b6bc5fdb6a0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxjb21tZXJjaWFsJTIwYWlycGxhbmUlMjBibHVlJTIwc2t5fGVufDB8fHx8MTc2ODcxNzU4OXww&ixlib=rb-4.1.0&q=85" 
            alt="cheap flights Delhi to Mumbai - AirVia subscription service" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/80 to-transparent"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <motion.div 
              className="lg:col-span-7"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-block mb-6">
                <span className="bg-amber-600/20 text-amber-500 px-4 py-2 rounded-full text-sm font-medium border border-amber-600/30">
                  India's First Flight Subscription Service
                </span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Get Customized <span className="text-amber-500">Flight Subscriptions</span> for Your Route
              </h1>
              <p className="text-xl text-slate-300 mb-8 leading-relaxed">
                Choose your sector, select your tickets. We create a personalized subscription plan just for you. 
                Save 40% on Delhi-Mumbai routes and other popular sectors.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/subscription-plans" data-testid="hero-subscribe-btn">
                  <Button className="rounded-full bg-amber-600 hover:bg-amber-700 text-white px-8 py-6 text-lg font-semibold btn-primary">
                    Get Your Custom Plan
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/faq" data-testid="hero-learn-more-btn">
                  <Button variant="outline" className="rounded-full px-8 py-6 text-lg border-white text-white hover:bg-white/10">
                    Learn More
                  </Button>
                </Link>
              </div>
            </motion.div>

            <motion.div 
              className="lg:col-span-5"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <div className="glass-effect rounded-2xl p-8 space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-amber-600 rounded-full flex items-center justify-center">
                    <Star className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">₹4,750-₹5,250</p>
                    <p className="text-slate-600 text-sm">Per ticket for subscribers</p>
                  </div>
                </div>
                <div className="border-t border-slate-200 pt-4">
                  <p className="text-sm text-slate-600 mb-2">Regular price: ₹6,900-₹7,500</p>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-green-600 font-semibold">Save ₹2,150-₹2,750 per booking</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Why Choose AirVia for Cheap Flights?</h2>
            <p className="text-lg text-slate-600">Experience affordable flight booking for Bengaluru, Hyderabad, Chennai, Pune, Mumbai, Delhi NCR, and Gurgaon routes</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: <Plane className="w-8 h-8" />,
                title: 'Annual Flight Pass',
                description: 'Perfect for frequent flyers in Bengaluru, Hyderabad, Chennai, and Pune. Get customized cheap flight tickets for your travel routes.'
              },
              {
                icon: <Shield className="w-8 h-8" />,
                title: 'Save 40% on Delhi-Mumbai Routes',
                description: 'Cheap flights subscription with guaranteed low rates. Book domestic flights at flat rates throughout the year with significant savings.'
              },
              {
                icon: <Clock className="w-8 h-8" />,
                title: 'Quick Booking Process',
                description: 'Easy flight booking for IndiGo, Air India, and other domestic airlines. Get personalized quote within 24 hours for your sector.'
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                <Card className="p-8 hover-lift border-slate-200 h-full" data-testid={`feature-card-${index}`}>
                  <div className="w-16 h-16 bg-sky-500 rounded-2xl flex items-center justify-center mb-6 text-white">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{feature.description}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">How It Works</h2>
            <p className="text-lg text-slate-600">Get started in 3 simple steps</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                title: 'Choose Your Plan',
                description: 'Select a subscription plan that fits your travel needs. Basic, Pro, or Elite.'
              },
              {
                step: '02',
                title: 'Complete Payment',
                description: 'Secure one-time payment for the subscription period. All major payment methods accepted.'
              },
              {
                step: '03',
                title: 'Start Flying',
                description: 'Search and book flights at flat rates. Enjoy priority access and premium benefits.'
              }
            ].map((step, index) => (
              <motion.div
                key={index}
                className="relative"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                <div className="text-center" data-testid={`how-it-works-step-${index}`}>
                  <div className="text-6xl font-bold text-amber-600/20 mb-4">{step.step}</div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{step.title}</h3>
                  <p className="text-slate-600">{step.description}</p>
                </div>
                {index < 2 && (
                  <div className="hidden md:block absolute top-12 right-0 transform translate-x-1/2">
                    <ArrowRight className="w-8 h-8 text-amber-600/30" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">What Our Customers Say</h2>
            <p className="text-lg text-slate-600">Join thousands of happy travelers</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: 'Priya Sharma',
                role: 'Business Consultant',
                content: 'AirVia has transformed my business travel. I save over ₹30,000 annually and the booking process is seamless!',
                rating: 5
              },
              {
                name: 'Rajesh Kumar',
                role: 'Entrepreneur',
                content: 'The Pro plan is perfect for my monthly Mumbai-Delhi trips. Fixed rates mean no surprise costs.',
                rating: 5
              },
              {
                name: 'Ananya Iyer',
                role: 'Travel Blogger',
                content: 'Elite plan subscriber here! Priority boarding and lounge access make every journey premium.',
                rating: 5
              }
            ].map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                <Card className="p-6 hover-lift border-slate-200 h-full" data-testid={`testimonial-${index}`}>
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-amber-500 text-amber-500" />
                    ))}
                  </div>
                  <p className="text-slate-700 mb-6 italic">"{testimonial.content}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-slate-900 rounded-full flex items-center justify-center">
                      <Users className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{testimonial.name}</p>
                      <p className="text-sm text-slate-600">{testimonial.role}</p>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Routes Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Popular Flight Routes</h2>
            <p className="text-lg text-slate-600">Flat-rate subscriptions on India's busiest domestic routes</p>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[
              { from: 'Bangalore', to: 'Delhi', slug: 'bangalore-to-delhi', price: '8,500' },
              { from: 'Mumbai', to: 'Delhi', slug: 'mumbai-to-delhi', price: '7,200' },
              { from: 'Hyderabad', to: 'Bangalore', slug: 'hyderabad-to-bangalore', price: '4,300' },
              { from: 'Chennai', to: 'Delhi', slug: 'chennai-to-delhi', price: '8,800' },
              { from: 'Delhi', to: 'Mumbai', slug: 'delhi-to-mumbai', price: '7,200' },
              { from: 'Pune', to: 'Delhi', slug: 'pune-to-delhi', price: '6,500' },
              { from: 'Hyderabad', to: 'Delhi', slug: 'hyderabad-to-delhi', price: '6,800' },
              { from: 'Kolkata', to: 'Delhi', slug: 'kolkata-to-delhi', price: '6,100' },
            ].map((route, index) => (
              <motion.div
                key={route.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <Link to={`/flights/${route.slug}`} data-testid={`route-link-${route.slug}`}>
                  <Card className="p-4 border-slate-200 hover:border-amber-400 transition-all hover:shadow-md cursor-pointer">
                    <p className="font-semibold text-slate-900 text-sm">{route.from} → {route.to}</p>
                    <p className="text-green-600 text-sm font-medium mt-1">From ₹{route.price}</p>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-slate-300 mb-8">
              Tell us your route and ticket needs for a customized subscription plan
            </p>
            <Link to="/subscription-plans" data-testid="cta-subscribe-btn">
              <Button className="rounded-full bg-amber-600 hover:bg-amber-700 text-white px-10 py-6 text-lg font-semibold btn-primary">
                Get Your Custom Plan
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
